import React, { useState } from 'react';
import styled from 'styled-components';

import Body2 from '@joinforma/ui/components/Body2';
import Button from '@joinforma/ui/components/Button';
import { ButtonVariant, ButtonSize } from '@joinforma/ui/constants/properties';
import { ScreenWidth } from '@joinforma/ui/constants/breakpoints';
import { ReactComponent as CreditCardSvg } from '@joinforma/ui/assets/icons/credit-card.svg';

import CardActionModal from './CardActionModal';
import useSnackbar from '@joinforma/ui/hooks/useSnackbar';

import { FormaCardMember, FormaCard } from './types';
import DebitCard from 'components/common/DebitCard';

import useMutationCreateFormaCard from 'hooks/useMutationCreateFormaCard';
import IssueFormaCardForm from './IssueFormaCardForm';

interface CardDetails {
  cardHolderType: FormaCard['cardHolderType'];
  cardHolderReferenceId: FormaCard['cardHolderId'];
  cardHolderDetails: {
    fullName: FormaCard['cardHolderDetails']['fullName'];
    dateOfBirth?: FormaCardMember['date_of_birth'];
    address: FormaCard['cardHolderDetails']['billingAddress'];
    shippingAddress?: FormaCard['cardHolderDetails']['shippingAddress'];
    phone: {
      countryCode: string;
      phoneNumber: string;
    };
    email?: FormaCardMember['email'];
  };
}

interface CardActionButtonProps {
  highlighted?: boolean;
}

interface Props {
  cardType: string;
  description: string;
  forma_card_member: FormaCardMember;
  employee_id: FormaCardMember['employee_id'];
  refetchCards: () => void;
}

const Container = styled.div``;

const CardInformationContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  margin: 32px 0 64px;
  @media (max-width: ${ScreenWidth.TABLET_PORTRAIT}) {
    flex-direction: column;
  }
`;

const ReissueCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 0 0 27px;
  @media (max-width: ${ScreenWidth.TABLET_PORTRAIT}) {
    width: 277px;
    margin: 25px 0 0;
  }
`;

const Description = styled(Body2)`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const LeftColumn = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const CardActionButton = styled(Button).attrs(
  (props: CardActionButtonProps) => ({
    variant: props.highlighted
      ? ButtonVariant.PRIMARY
      : ButtonVariant.SECONDARY,
    size: ButtonSize.SMALL,
  })
)<CardActionButtonProps>`
  width: 277px;
  height: 48px;
  margin: 12px 0;
  font-size: 15px;
  line-height: 15px;
`;

const ReissueCard = ({
  forma_card_member,
  cardType,
  description,
  employee_id,
  refetchCards,
}: Props) => {
  const [reissue_modal, setIsReissueModalOpen] = useState(false);
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbar();

  const { mutate: reissueCard } = useMutationCreateFormaCard({
    onSuccess: () => {
      refetchCards();
      showSuccessSnackbar({
        text: 'A new card has been mailed to the address you provided',
      });
    },
    onError: () => {
      showErrorSnackbar({ text: 'An error occurred. Please try again.' });
    },
  });
  const handleCreateFormaCard = ({
    card_details,
  }: {
    card_details: CardDetails;
  }) => {
    reissueCard({ card_details, card_type: cardType });
  };
  const handleCancel = () => setIsReissueModalOpen(false);

  return (
    <Container>
      <CardInformationContainer>
        <LeftColumn>
          <DebitCard card_type={cardType} is_locked={false} />
        </LeftColumn>
        <ReissueCardContainer>
          <CardActionButton
            highlighted
            startIcon={CreditCardSvg}
            onClick={() => setIsReissueModalOpen(true)}
          >
            Re-issue card
          </CardActionButton>
          <Description>{description}</Description>
        </ReissueCardContainer>
      </CardInformationContainer>
      <CardActionModal
        is_open={reissue_modal}
        title={'Re-issue your card'}
        onCancel={() => setIsReissueModalOpen(false)}
      >
        <IssueFormaCardForm
          forma_card_member={forma_card_member}
          employee_id={employee_id}
          handleCreateFormaCard={handleCreateFormaCard}
          handleCancel={handleCancel}
        />
      </CardActionModal>
    </Container>
  );
};

export default ReissueCard;
